import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const STEP_CONFIG = {
  CIRCLE_SIZE: 8,
  ANIMATION_DURATION: 0.5,
  ANIMATION_EASE: 'easeOut'
} as const;

const getStepPositions = (index: number, totalSteps: number) => {
  const stepWidth = 100 / (totalSteps - 1);
  const leftPosition = `${stepWidth * index}% + ${STEP_CONFIG.CIRCLE_SIZE}px`;
  const rightPosition = `${100 - stepWidth * (index + 1)}% + ${STEP_CONFIG.CIRCLE_SIZE}px`;

  return {
    left: `calc(${leftPosition})`,
    right: `calc(${rightPosition})`
  };
};

interface Step {
  id: string;
  label: string; // Kept for internal reference
  description?: string;
  disabled?: boolean;
}

interface StepCircleProps {
  isActive: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
}

function StepCircle({ isActive, isCompleted, isDisabled }: StepCircleProps) {
  return (
    <div className="relative">
      <motion.div
        className={cn(
          'flex items-center justify-center w-4 h-4 rounded-full relative',
          isActive && 'bg-primary shadow-sm',
          isCompleted && 'bg-primary',
          !isActive && !isCompleted && !isDisabled && 'bg-muted-foreground/30',
          isDisabled && 'bg-muted-foreground/20',
          isActive &&
            !isDisabled &&
            'after:absolute after:inset-0 after:rounded-full after:animate-ping after:bg-primary/40'
        )}
        animate={isActive ? { scale: 1.1 } : { scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {isCompleted && <Check className="h-2 w-2 text-background stroke-[3]" />}
      </motion.div>
    </div>
  );
}

interface StepButtonProps {
  step: Step;
  isActive: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
  onStepClick?: (stepId: string) => void;
}

function StepButton({ step, isActive, isCompleted, isDisabled, onStepClick }: StepButtonProps) {
  return (
    <div className="flex items-center relative z-10">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => !isDisabled && onStepClick?.(step.id)}
              disabled={isDisabled || !onStepClick}
              className={cn(
                'group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary rounded-full',
                onStepClick && !isDisabled && 'cursor-pointer',
                isDisabled && 'cursor-not-allowed opacity-70'
              )}
            >
              <StepCircle isActive={isActive} isCompleted={isCompleted} isDisabled={isDisabled} />
            </button>
          </TooltipTrigger>
          {step.description && (
            <TooltipContent>
              <p>{isDisabled ? 'Complete previous steps to unlock' : step.description}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

interface StepConnectorProps {
  index: number;
  totalSteps: number;
  isCompleted: boolean;
  isActive: boolean;
  isNextStepDisabled: boolean;
}

function StepConnector({
  index,
  totalSteps,
  isCompleted,
  isActive,
  isNextStepDisabled
}: StepConnectorProps) {
  if (index === totalSteps - 1) return null;
  if (!((isCompleted || isActive) && !isNextStepDisabled)) return null;

  return (
    <div
      className="absolute top-2 h-[1px] overflow-hidden"
      style={getStepPositions(index, totalSteps)}
    >
      <motion.div
        className={cn('h-full w-full', isCompleted ? 'bg-primary' : 'bg-muted')}
        initial={{ width: isActive ? '0%' : '100%' }}
        animate={{ width: '100%' }}
        transition={{
          duration: STEP_CONFIG.ANIMATION_DURATION,
          ease: STEP_CONFIG.ANIMATION_EASE
        }}
      />
    </div>
  );
}

interface StepsProps {
  steps: Step[];
  currentStep: string;
  onStepClick?: (stepId: string) => void;
  className?: string;
  title?: string;
}

export function Steps({ steps, currentStep, onStepClick, className, title }: StepsProps) {
  const currentIndex = steps.findIndex((step) => step.id === currentStep);

  return (
    <div className={cn('flex flex-col w-full max-w-2xl mx-auto', className)}>
      {title && (
        <div className="mb-6">
          <h2 className="text-lg font-medium">{title}</h2>
        </div>
      )}

      <div className="flex flex-col items-center w-full">
        <div className="flex items-center justify-between w-full max-w-md relative">
          {steps.map((_, index) => {
            const isCompleted = index < currentIndex;
            const isActive = index === currentIndex;
            const nextStepDisabled = index + 1 < steps.length && steps[index + 1].disabled === true;

            return (
              <StepConnector
                key={`connector-${index}`}
                index={index}
                totalSteps={steps.length}
                isCompleted={isCompleted}
                isActive={isActive}
                isNextStepDisabled={nextStepDisabled}
              />
            );
          })}

          <div className="flex justify-between w-full">
            {steps.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = index < currentIndex;
              const isDisabled = step.disabled === true;

              return (
                <StepButton
                  key={step.id}
                  step={step}
                  isActive={isActive}
                  isCompleted={isCompleted}
                  isDisabled={isDisabled}
                  onStepClick={onStepClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
